import React, { useState, useMemo, useEffect } from 'react';
import { PALETTE } from './SiteLayout';
import { usePricing } from '../../i18n/pricing';

/**
 * Conservative cost-of-inaction calculator for Pricing page.
 * Three inputs → annualised hidden cost of staying on spreadsheets.
 *
 * Numbers are deliberately conservative, chosen so the result is defensible
 * even to a sceptical CFO. All monetary defaults come from usePricing() so
 * the calculator adapts to EUR / PLN / HUF.
 */
export default function CostOfInactionCalculator() {
  const p = usePricing();

  const money = useMemo(
    () => new Intl.NumberFormat(p.locale || 'en-IE', {
      style: 'currency', currency: p.code, maximumFractionDigits: 0, minimumFractionDigits: 0,
    }),
    [p.locale, p.code],
  );
  const fmt = (n) => money.format(n);

  // Sensible slider bounds scale with the base avg-order-value so sliders
  // are usable in all markets (HUF values are 100× EUR).
  const avgStep = p.code === 'HUF' ? 5000 : p.code === 'PLN' ? 50 : 50;
  const avgMin  = Math.round(p.avgOrderValue * 0.2);
  const avgMax  = Math.round(p.avgOrderValue * 4);

  const [orders, setOrders]     = useState(40);              // dispatches per week
  const [avgValue, setAvgValue] = useState(p.avgOrderValue); // avg order value in local ccy
  const [auditRisk, setAuditRisk] = useState(15);            // % chance of a recall/audit incident this year

  // If the visitor switches market mid-session, reseed the currency-anchored
  // slider to that market's sensible default so it doesn't cap at the old
  // market's slider bounds (e.g. EUR 850 doesn't make sense on a HUF slider).
  useEffect(() => {
    setAvgValue(p.avgOrderValue);
  }, [p.code, p.avgOrderValue]);

  const calc = useMemo(() => {
    // 1. Admin time saved: each dispatch in Excel ≈ 7 min vs ≈ 1 min in Keystone.
    //    6 min × orders/week × 50 weeks × local loaded staff hourly cost.
    const minutesSavedPerWeek = orders * 6;
    const adminHoursPerYear = (minutesSavedPerWeek * 50) / 60;
    const adminCost = Math.round(adminHoursPerYear * p.hourlyStaffCost);

    // 2. Billing-error leakage: industry research suggests 0.6–1.2% of revenue
    //    leaks through pick/weight/code errors in spreadsheet ops. Use 0.7%.
    const annualRevenue = orders * avgValue * 50;
    const leakage = Math.round(annualRevenue * 0.007);

    // 3. Audit/recall exposure: weighted expected loss.
    const exposure = Math.round((auditRisk / 100) * p.auditIncidentCost);

    const total = adminCost + leakage + exposure;

    return {
      adminCost,
      adminHoursPerYear: Math.round(adminHoursPerYear),
      leakage,
      annualRevenue,
      exposure,
      total,
    };
  }, [orders, avgValue, auditRisk, p.hourlyStaffCost, p.auditIncidentCost]);

  return (
    <section style={{ background: PALETTE.cream, padding: '110px 32px', borderTop: `1px solid ${PALETTE.border}` }} data-testid="cost-of-inaction-calculator">
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 14,
          }}>What "doing nothing" actually costs</div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: PALETTE.navy,
            margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
          }}>
            The cost of <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>nothing changing.</em>
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: PALETTE.slate, marginTop: 16, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Not a sales gimmick. Conservative numbers, drawn from how artisan dairies actually leak money on spreadsheets, admin time, weight/code billing errors, and audit/recall exposure. All figures in <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>{p.code}</strong>. Adjust to your own operation.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: 0,
          border: `1px solid ${PALETTE.border}`,
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }} className="coi-grid">
          {/* Inputs */}
          <div style={{ padding: 36, borderRight: `1px solid ${PALETTE.border}` }} className="coi-inputs">
            <div style={{
              fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700, color: PALETTE.muted, marginBottom: 24,
            }}>Your operation</div>

            <Slider
              label="Dispatches per week"
              value={orders}
              onChange={setOrders}
              min={5} max={200} step={5}
              suffix={`${orders} dispatches`}
              hint="Most artisan dairies run 20–80/week."
              testid="coi-orders"
            />
            <Slider
              label="Average order value"
              value={avgValue}
              onChange={setAvgValue}
              min={avgMin} max={avgMax} step={avgStep}
              suffix={fmt(avgValue)}
              hint="Wholesale invoice line, before VAT."
              testid="coi-value"
            />
            <Slider
              label="Audit / recall risk this year"
              value={auditRisk}
              onChange={setAuditRisk}
              min={0} max={50} step={1}
              suffix={`${auditRisk}%`}
              hint="Honest probability of a national food-authority investigation (FSAI, NÉBIH, GIS, BVL, DGAV, etc.), retailer compliance hold, or batch query."
              testid="coi-risk"
            />
          </div>

          {/* Results */}
          <div style={{
            padding: 36,
            background: PALETTE.navy,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}>
            <div style={{
              fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700, color: PALETTE.gold,
            }}>Annual hidden cost</div>

            <div style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(40px, 5.5vw, 66px)',
              fontWeight: 700, color: '#fff',
              letterSpacing: '-0.03em', lineHeight: 1,
            }} data-testid="coi-total">
              {fmt(calc.total)}
            </div>
            <div style={{ fontSize: 13, color: '#CBD5E1', marginTop: -6, fontStyle: 'italic' }}>
              per year, money currently leaving the business invisibly.
            </div>

            <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.10)', paddingTop: 18 }}>
              <Row label="Admin time on dispatch / paperwork" sub={`≈ ${calc.adminHoursPerYear} hours/year @ ${fmt(p.hourlyStaffCost)}/hr`} value={fmt(calc.adminCost)} />
              <Row label="Pick/weight/code billing leakage" sub="≈ 0.7% of dispatch revenue" value={fmt(calc.leakage)} />
              <Row label="Audit / recall exposure" sub={`${auditRisk}% × ${fmt(p.auditIncidentCost)} incident cost`} value={fmt(calc.exposure)} />
            </div>

            <div style={{
              marginTop: 18,
              padding: '14px 16px',
              background: 'rgba(201, 169, 97, 0.10)',
              border: `1px solid ${PALETTE.gold}40`,
              borderRadius: 6,
              fontSize: 13.5,
              lineHeight: 1.55,
              color: '#E5E7EB',
            }}>
              <strong style={{ color: PALETTE.gold, fontWeight: 700 }}>What it means:</strong> this is the money already leaving the business every year. A right-sized Keystone quote is measured against this number, not against a headline price.
              <br/>
              Bring your figures to the call and we will show you where the licence lands relative to them.
            </div>
          </div>
        </div>

        <div style={{
          marginTop: 18,
          fontSize: 12,
          color: PALETTE.muted,
          fontStyle: 'italic',
          textAlign: 'center',
          lineHeight: 1.6,
        }}>
          Calculation method: 6 minutes saved per dispatch × loaded staff cost ({fmt(p.hourlyStaffCost)}/hr); 0.7% revenue leakage (industry research, deliberately at the low end of 0.6–1.2%); audit/recall exposure weighted at {fmt(p.auditIncidentCost)} incident cost (national food-authority investigation + retailer hold + lost margin).
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .coi-grid { grid-template-columns: 1fr !important; }
          .coi-inputs { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; }
        }
      `}</style>
    </section>
  );
}

function Slider({ label, value, onChange, min, max, step, suffix, hint, testid }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <label style={{ fontSize: 14, fontWeight: 600, color: PALETTE.ink }}>{label}</label>
        <span style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 18, fontWeight: 700, color: PALETTE.magenta,
          letterSpacing: '-0.01em',
        }}>{suffix}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        style={{
          width: '100%',
          accentColor: PALETTE.magenta,
          height: 4,
        }}
      />
      <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 6, fontStyle: 'italic', lineHeight: 1.5 }}>{hint}</div>
    </div>
  );
}

function Row({ label, sub, value }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 16,
      padding: '8px 0',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    }}>
      <div>
        <div style={{ fontSize: 13.5, color: '#fff', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2, fontStyle: 'italic' }}>{sub}</div>
      </div>
      <div style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 18, fontWeight: 700, color: PALETTE.gold,
        letterSpacing: '-0.01em',
      }}>{value}</div>
    </div>
  );
}
